import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'
import Badge from '@/components/common/Badge'
import {
  listAiLabModels,
  fetchAiLabSources,
  startAiLabRun,
} from '@/lib/aiLabClient'
import {
  DEFAULT_AI_LAB_MODELS,
  DEFAULT_AI_LAB_LEVEL,
} from '@/lib/config'

const SCENARIOS = [
  {
    id: 'association-discovery',
    title: 'Association Discovery',
    description: 'Find Minnesota associations with Squirt B1 programs and official homepages.',
  },
  {
    id: 'homepage-navigation',
    title: 'Homepage Navigation',
    description: 'Locate calendar or schedule links directly from the association homepage.',
  },
  {
    id: 'calendar-structure',
    title: 'Calendar Structure',
    description: 'Determine if the association offers master calendars or team-level pages.',
  },
  {
    id: 'ical-discovery',
    title: 'iCal Discovery',
    description: 'Identify subscription feeds (iCal/webcal) for automated syncing.',
  },
  {
    id: 'list-view-detection',
    title: 'List View Detection',
    description: 'Confirm whether an agenda/list calendar view is available for scraping.',
  },
  {
    id: 'calendar-extraction',
    title: 'Calendar Extraction',
    description: 'Extract structured events (date/time/type/location) for Squirt B1 teams.',
  },
  {
    id: 'team-enumeration',
    title: 'Team Enumeration',
    description: 'Discover all Squirt B1 teams within an association (e.g., Black, Gold, Purple).',
  },
]

const PROMPT_TOKEN_ESTIMATE = 1200
const COMPLETION_TOKEN_ESTIMATE = 800

const estimateCostForModel = (model) => {
  const promptRate = Number(model.pricing?.prompt ?? 0)
  const completionRate = Number(model.pricing?.completion ?? 0)
  const requestRate = Number(model.pricing?.request ?? 0)
  return promptRate * PROMPT_TOKEN_ESTIMATE + completionRate * COMPLETION_TOKEN_ESTIMATE + requestRate
}

function AILabRun() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [models, setModels] = useState([])
  const [associations, setAssociations] = useState([])
  const [submitting, setSubmitting] = useState(false)

  const [selectedModels, setSelectedModels] = useState(() => new Set(DEFAULT_AI_LAB_MODELS))
  const [selectedScenarios, setSelectedScenarios] = useState(() => new Set(SCENARIOS.map((scenario) => scenario.id)))
  const [selectedAssociations, setSelectedAssociations] = useState(new Set())

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [modelData, associationData] = await Promise.all([
          listAiLabModels(),
          fetchAiLabSources(),
        ])
        setModels(modelData)
        setAssociations(associationData)
        setSelectedAssociations(new Set(associationData.map((item) => item.homepage_url)))
      } catch (err) {
        console.error('Failed to load AI Lab configuration data', err)
        toast.error('Unable to load configuration data')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  const selectedModelObjects = useMemo(
    () => models.filter((model) => selectedModels.has(model.id)),
    [models, selectedModels]
  )

  const requestCount =
    selectedModels.size * selectedScenarios.size * Math.max(selectedAssociations.size, associations.length ? 1 : 0)

  const estimatedCost = useMemo(() => {
    if (selectedModels.size === 0 || selectedScenarios.size === 0 || selectedAssociations.size === 0) {
      return 0
    }

    const perScenarioCost = selectedModelObjects.reduce((sum, model) => sum + estimateCostForModel(model), 0)
    return Number((perScenarioCost * selectedScenarios.size * selectedAssociations.size).toFixed(4))
  }, [selectedModelObjects, selectedScenarios.size, selectedAssociations.size])

  const toggleSelection = (setFn, currentSet, value) => {
    const next = new Set(currentSet)
    if (next.has(value)) {
      next.delete(value)
    } else {
      next.add(value)
    }
    setFn(next)
  }

  const selectAllAssociations = () => {
    setSelectedAssociations(new Set(associations.map((association) => association.homepage_url)))
  }

  const clearAssociations = () => setSelectedAssociations(new Set())

  const handleStartRun = async () => {
    if (selectedModels.size === 0 || selectedScenarios.size === 0 || selectedAssociations.size === 0) {
      toast.error('Select at least one model, scenario, and association to start a run.')
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        models: Array.from(selectedModels),
        scenarios: Array.from(selectedScenarios),
        associations: Array.from(selectedAssociations),
        level: DEFAULT_AI_LAB_LEVEL,
      }

      const { runId } = await startAiLabRun(payload)
      toast.success('Run launched. Redirecting to results...')
      navigate(`/admin/ai-lab/results?runId=${runId}`)
    } catch (err) {
      console.error('Failed to start AI Lab run', err)
      toast.error(err.message || 'Failed to start AI Lab run')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Container>
        <Card className="p-12">
          <Loading text="Preparing AI Lab configuration..." />
        </Card>
      </Container>
    )
  }

  return (
    <Container className="py-10 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-brand-slate">Configure AI Lab Run</h1>
          <p className="text-brand-slate-dark mt-2 max-w-2xl">
            Select the models, scenarios, and associations for this evaluation. The run executes {selectedScenarios.size}{' '}
            scenarios per association for each model ({requestCount.toLocaleString()} total requests).
          </p>
        </div>
        <Badge variant="green">Level: {DEFAULT_AI_LAB_LEVEL}</Badge>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-brand-slate">Models</h2>
            <span className="text-xs text-brand-slate-dark">
              {selectedModels.size} selected of {models.length}
            </span>
          </div>
          <div className="space-y-3 max-h-[480px] overflow-y-auto pr-2 scrollbar-thin">
            {models.map((model) => {
              const checked = selectedModels.has(model.id)
              return (
                <label
                  key={model.id}
                  className={`flex items-start gap-3 border border-brand-gray-100 rounded-lg p-4 cursor-pointer transition hover:border-brand-blue/40 ${checked ? 'bg-brand-gray-50 border-brand-blue/40' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelection(setSelectedModels, selectedModels, model.id)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-brand-slate text-sm">{model.name}</div>
                    <div className="text-xs text-brand-slate-dark">
                      {model.provider || 'Unknown provider'} · Prompt {formatCurrency(model.pricing?.prompt)} · Completion{' '}
                      {formatCurrency(model.pricing?.completion)}
                    </div>
                  </div>
                </label>
              )
            })}
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-brand-slate">Scenarios</h2>
            <span className="text-xs text-brand-slate-dark">{selectedScenarios.size} of {SCENARIOS.length} enabled</span>
          </div>
          <div className="space-y-3">
            {SCENARIOS.map((scenario) => {
              const checked = selectedScenarios.has(scenario.id)
              return (
                <label
                  key={scenario.id}
                  className={`flex items-start gap-3 border border-brand-gray-100 rounded-lg p-4 cursor-pointer transition hover:border-brand-blue/40 ${checked ? 'bg-brand-gray-50 border-brand-blue/40' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelection(setSelectedScenarios, selectedScenarios, scenario.id)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-brand-slate text-sm">{scenario.title}</div>
                    <div className="text-xs text-brand-slate-dark">{scenario.description}</div>
                  </div>
                </label>
              )
            })}
          </div>
        </Card>
      </div>

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-brand-slate">Associations</h2>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={selectAllAssociations}>Select All</Button>
            <Button variant="secondary" onClick={clearAssociations}>Clear</Button>
          </div>
        </div>
        {associations.length === 0 ? (
          <div className="text-sm text-brand-slate-dark">No associations configured yet.</div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {associations.map((association) => {
              const { homepage_url: homepageUrl } = association
              const checked = selectedAssociations.has(homepageUrl)
              return (
                <label
                  key={homepageUrl}
                  className={`flex items-start gap-3 border border-brand-gray-100 rounded-lg p-4 cursor-pointer transition hover:border-brand-blue/40 ${checked ? 'bg-brand-gray-50 border-brand-blue/40' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelection(setSelectedAssociations, selectedAssociations, homepageUrl)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-brand-slate text-sm">{association.name}</div>
                    <div className="text-xs text-brand-slate-dark break-all">{homepageUrl}</div>
                    {association.region && (
                      <div className="text-[11px] text-brand-slate-dark/80 mt-1">Region: {association.region}</div>
                    )}
                  </div>
                </label>
              )
            })}
          </div>
        )}
      </Card>

      <Card className="p-6 space-y-4">
        <h2 className="text-xl font-semibold text-brand-slate">Summary</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-brand-gray-50 rounded-lg p-4">
            <div className="text-sm text-brand-slate-dark">Estimated Requests</div>
            <div className="text-2xl font-semibold text-brand-slate mt-1">{requestCount.toLocaleString()}</div>
            <div className="text-xs text-brand-slate-dark mt-1">
              {selectedModels.size} models × {selectedScenarios.size} scenarios × {selectedAssociations.size} associations
            </div>
          </div>
          <div className="bg-brand-gray-50 rounded-lg p-4">
            <div className="text-sm text-brand-slate-dark">Estimated Spend</div>
            <div className="text-2xl font-semibold text-brand-slate mt-1">${estimatedCost.toFixed(4)}</div>
            <div className="text-xs text-brand-slate-dark mt-1">
              Based on {PROMPT_TOKEN_ESTIMATE.toLocaleString()} prompt tokens and {COMPLETION_TOKEN_ESTIMATE.toLocaleString()} completion tokens per request.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleStartRun}
            disabled={
              submitting ||
              selectedModels.size === 0 ||
              selectedScenarios.size === 0 ||
              selectedAssociations.size === 0
            }
            loading={submitting}
          >
            Launch Run
          </Button>
          <Button variant="secondary" onClick={() => navigate('/admin/ai-lab')} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </Card>
    </Container>
  )
}

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—'
  return `$${Number(value).toFixed(6)}`
}

export default AILabRun


